import React from "react";
import { Link } from "react-router-dom";
import logo from "../imgs/logo.png";

const PrivacyPolicy = () => {
  return (
    <div className="legal-page">
      {/* Navigation Header */}
      <div className="legal-nav">
        <div className="legal-nav-container">
          <Link to="/" className="back-button">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
              <path d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
            </svg>
            Back
          </Link>

          <div className="legal-nav-brand">
            <img src={logo} alt="Viralz Logo" className="legal-nav-logo" />
            <span className="legal-nav-title">Viralz</span>
          </div>
        </div>
      </div>

      <div className="legal-container">
        <h1>Privacy Policy</h1>
        <div className="legal-meta">
          <p>
            <strong>Effective Date:</strong> January 1, 2025
          </p>
          <p>
            <strong>Last Updated:</strong> January 1, 2025
          </p>
        </div>

        <section>
          <h2>1. INTRODUCTION</h2>
          <p>
            Welcome to Viralz ("we," "our," or "us"). This Privacy Policy
            explains how we collect, use, disclose, and safeguard your
            information when you use our mobile application and website
            (collectively, the "Service"). Viralz is a TikTok analytics and
            content optimization platform that helps creators improve their
            social media performance.
          </p>
        </section>

        <section>
          <h2>2. INFORMATION WE COLLECT</h2>

          <h3>2.1 Personal Information </h3>
          <ul>
            <li>
              <strong>Account Information:</strong> Email address,
              authentication credentials from Apple Sign-In or Google Sign-In
            </li>
            <li>
              <strong>TikTok Profile Information:</strong> TikTok username,
              profile data, follower count, engagement metrics
            </li>
            <li>
              <strong>Contact Information:</strong> Email address for support
              and communications
            </li>
          </ul>

          <h3>2.2 TikTok Data</h3>
          <ul>
            <li>
              <strong>Profile Analytics:</strong> Follower count, following
              count, total likes, video count, engagement rates
            </li>
            <li>
              <strong>Video Performance:</strong> View counts, like ratios,
              comment ratios, share ratios
            </li>
            <li>
              <strong>Content Analysis:</strong> Video metadata, hashtags,
              descriptions, performance metrics
            </li>
            <li>
              <strong>Historical Data:</strong> Weekly growth trends,
              performance history
            </li>
          </ul>

          <h3>2.3 Usage Data</h3>
          <ul>
            <li>
              <strong>App Analytics:</strong> Screen views, feature usage,
              session duration
            </li>
            <li>
              <strong>Device Information:</strong> Platform (iOS/Android),
              device model, app version
            </li>
            <li>
              <strong>Performance Data:</strong> Profile scores, AI analysis
              results, content insights
            </li>
          </ul>

          <h3>2.4 Automatically Collected Information</h3>
          <ul>
            <li>
              <strong>Technical Data:</strong> IP address, device identifiers,
              operating system version
            </li>
            <li>
              <strong>Analytics Data:</strong> App usage patterns, feature
              interactions, error logs
            </li>
            <li>
              <strong>Session Data:</strong> Login times, session duration, app
              interactions
            </li>
          </ul>
        </section>

        <section>
          <h2>3. HOW WE USE YOUR INFORMATION</h2>

          <h3>3.1 Primary Services</h3>
          <ul>
            <li>
              <strong>TikTok Analysis:</strong> Analyze your TikTok profile and
              content performance
            </li>
            <li>
              <strong>AI Insights:</strong> Provide personalized content
              recommendations and insights
            </li>
            <li>
              <strong>Performance Tracking:</strong> Monitor your growth and
              engagement metrics
            </li>
            <li>
              <strong>Content Optimization:</strong> Suggest improvements for
              better viral potential
            </li>
          </ul>

          <h3>3.2 Service Improvement</h3>
          <ul>
            <li>
              <strong>Analytics:</strong> Understand how users interact with our
              app
            </li>
            <li>
              <strong>Feature Development:</strong> Improve existing features
              and develop new ones
            </li>
            <li>
              <strong>Performance Optimization:</strong> Enhance app speed and
              reliability
            </li>
            <li>
              <strong>Bug Fixes:</strong> Identify and resolve technical issues
            </li>
          </ul>

          <h3>3.3 Communication</h3>
          <ul>
            <li>
              <strong>Support:</strong> Respond to your inquiries and provide
              customer service
            </li>
            <li>
              <strong>Updates:</strong> Notify you about app updates and new
              features
            </li>
            <li>
              <strong>Marketing:</strong> Send promotional content (with your
              consent)
            </li>
          </ul>
        </section>

        <section>
          <h2>4. INFORMATION SHARING</h2>

          <h3>4.1 Third-Party Services</h3>
          <ul>
            <li>
              <strong>Analytics Providers:</strong> PostHog, Mixpanel, Amplitude
              for usage analytics
            </li>
            <li>
              <strong>Authentication:</strong> Apple Sign-In, Google Sign-In for
              account creation
            </li>
            <li>
              <strong>Payment Processing:</strong> Superwall for subscription
              management
            </li>
            <li>
              <strong>Error Tracking:</strong> Sentry for crash reporting and
              error monitoring
            </li>
            <li>
              <strong>Cloud Services:</strong> Data storage and processing
              services
            </li>
          </ul>

          <h3>4.2 Legal Requirements</h3>
          <p>
            We may disclose your information if required by law, regulation, or
            legal process.
          </p>

          <h3>4.3 Business Transfers</h3>
          <p>
            In the event of a merger, acquisition, or sale of assets, your
            information may be transferred.
          </p>
        </section>

        <section>
          <h2>5. DATA RETENTION</h2>
          <ul>
            <li>
              <strong>Account Data:</strong> Retained while your account is
              active
            </li>
            <li>
              <strong>Analytics Data:</strong> Retained for up to 2 years for
              service improvement
            </li>
            <li>
              <strong>TikTok Data:</strong> Refreshed regularly to maintain
              accuracy
            </li>
            <li>
              <strong>Support Data:</strong> Retained for 1 year after
              resolution
            </li>
          </ul>
        </section>

        <section>
          <h2>6. DATA SECURITY</h2>
          <p>
            We implement appropriate technical and organizational measures to
            protect your information:
          </p>
          <ul>
            <li>
              <strong>Encryption:</strong> Data encrypted in transit and at rest
            </li>
            <li>
              <strong>Access Controls:</strong> Limited access to authorized
              personnel only
            </li>
            <li>
              <strong>Regular Audits:</strong> Security assessments and
              vulnerability testing
            </li>
            <li>
              <strong>Secure Infrastructure:</strong> Cloud providers with
              industry-standard security
            </li>
          </ul>
        </section>

        <section>
          <h2>7. YOUR RIGHTS</h2>

          <h3>7.1 Access and Control</h3>
          <ul>
            <li>
              <strong>Access:</strong> Request a copy of your personal data
            </li>
            <li>
              <strong>Correction:</strong> Update or correct inaccurate
              information
            </li>
            <li>
              <strong>Deletion:</strong> Request deletion of your personal data
            </li>
            <li>
              <strong>Portability:</strong> Receive your data in a structured
              format
            </li>
          </ul>

          <h3>7.2 Account Management</h3>
          <ul>
            <li>
              <strong>Profile Settings:</strong> Manage your account information
            </li>
            <li>
              <strong>Data Export:</strong> Download your TikTok analytics data
            </li>
            <li>
              <strong>Account Deletion:</strong> Permanently delete your account
              and data
            </li>
          </ul>
        </section>

        <section>
          <h2>8. INTERNATIONAL TRANSFERS</h2>
          <p>
            Your information may be transferred to and processed in countries
            other than your own. We ensure appropriate safeguards are in place
            for such transfers.
          </p>
        </section>

        <section>
          <h2>9. CHILDREN'S PRIVACY</h2>
          <p>
            Our Service is not intended for users under 13 years of age. We do
            not knowingly collect personal information from children under 13.
          </p>
        </section>

        <section>
          <h2>10. CHANGES TO THIS POLICY</h2>
          <p>
            We may update this Privacy Policy from time to time. We will notify
            you of any changes by posting the new Privacy Policy on this page
            and updating the "Last Updated" date.
          </p>
        </section>

        <section>
          <h2>11. CONTACT US</h2>
          <p>
            If you have questions about this Privacy Policy, please contact us:
          </p>
          <ul>
            <li>
              <strong>Email:</strong> viralzmanagement@gmail.com
            </li>
            <li>
              <strong>Website:</strong> https://viralzapp.com/privacy
            </li>
          </ul>
        </section>
      </div>

      <style jsx>{`
        .legal-page {
          min-height: 100vh;
          background: #000000;
          color: #ffffff;
          font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
            sans-serif;
          padding: 0;
        }

        .legal-nav {
          background: rgba(0, 0, 0, 0.95);
          backdrop-filter: blur(20px);
          border-bottom: 1px solid rgba(254, 44, 85, 0.2);
          padding: 20px 0;
          position: sticky;
          top: 0;
          z-index: 100;
        }

        .legal-nav-container {
          max-width: 1200px;
          margin: 0 auto;
          padding: 0 20px;
          display: flex;
          justify-content: space-between;
          align-items: center;
        }

        .back-button {
          display: flex;
          align-items: center;
          gap: 8px;
          color: #ffffff;
          text-decoration: none;
          font-weight: 600;
          font-size: 1rem;
          padding: 10px 16px;
          border-radius: 10px;
          background: rgba(255, 255, 255, 0.1);
          border: 1px solid rgba(255, 255, 255, 0.2);
          transition: all 0.3s ease;
        }

        .back-button:hover {
          background: rgba(254, 44, 85, 0.2);
          border-color: #fe2c55;
          color: #fe2c55;
          transform: translateX(-2px);
        }

        .legal-nav-brand {
          display: flex;
          align-items: center;
          gap: 12px;
        }

        .legal-nav-logo {
          width: 32px;
          height: 32px;
          border-radius: 8px;
          background: rgba(254, 44, 85, 0.1);
          padding: 4px;
          border: 1px solid rgba(254, 44, 85, 0.3);
        }

        .legal-nav-title {
          font-size: 1.5rem;
          font-weight: 800;
          color: #fe2c55;
        }

        .legal-container {
          max-width: 800px;
          margin: 0 auto;
          line-height: 1.6;
          padding: 40px 20px;
        }

        h1 {
          font-size: 2.5rem;
          font-weight: 800;
          margin-bottom: 20px;
          background: linear-gradient(135deg, #fe2c55, #ff6b9d);
          -webkit-background-clip: text;
          -webkit-text-fill-color: transparent;
          background-clip: text;
        }

        .legal-meta {
          margin-bottom: 40px;
          padding: 20px;
          background: rgba(255, 255, 255, 0.05);
          border-radius: 10px;
          border: 1px solid rgba(254, 44, 85, 0.2);
        }

        .legal-meta p {
          margin: 5px 0;
          color: #cccccc;
        }

        section {
          margin-bottom: 40px;
        }

        h2 {
          font-size: 1.8rem;
          font-weight: 700;
          margin-bottom: 20px;
          color: #fe2c55;
        }

        h3 {
          font-size: 1.4rem;
          font-weight: 600;
          margin-bottom: 15px;
          color: #ffffff;
        }

        p {
          margin-bottom: 15px;
          color: #cccccc;
        }

        ul {
          margin-bottom: 20px;
          padding-left: 20px;
        }

        li {
          margin-bottom: 10px;
          color: #cccccc;
        }

        strong {
          color: #ffffff;
        }

        @media (max-width: 768px) {
          .legal-container {
            padding: 20px 15px;
          }

          .legal-nav-container {
            padding: 0 15px;
          }

          .legal-nav-title {
            font-size: 1.3rem;
          }

          .legal-nav-logo {
            width: 28px;
            height: 28px;
          }

          .back-button {
            padding: 8px 12px;
            font-size: 0.9rem;
          }

          h1 {
            font-size: 2rem;
          }

          h2 {
            font-size: 1.5rem;
          }

          h3 {
            font-size: 1.2rem;
          }
        }
      `}</style>
    </div>
  );
};

export default PrivacyPolicy;
